import { NOTIFICATION_CHANNEL } from "@nlc/shared-types";
import { SuprSend } from "@suprsend/react";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { env } from "@/config/env";
import { notificationsKeys } from "@/hooks/queryKeys";
import { useUser } from "@/hooks/useUser";

import { useConnectChannel } from "../../hooks/useConnectChannel";
import { useDisconnectChannel } from "../../hooks/useDisconnectChannel";
import { useGetSuprSendUser } from "../../hooks/useGetSuprSendUser";
import { useSlackIntegration } from "../../hooks/useSlackIntegration";
import { removeQueryString } from "../utils/removeQueryString";
import ConnectButton from "./ConnectButton";
import DisconnectButton from "./DisconnectButton";
import { ModalSlack } from "./ModalSlack";

export function SlackConfig({ isDisabled }: { isDisabled: boolean }) {
  const [loading, setLoading] = useState(false);
  const { code } = useSearch({
    from: "/_auth/_app-shell/notifications/settings",
  });
  const { mutate: updateSlack } = useSlackIntegration(code);
  const isMutating = useIsMutating({
    mutationKey: notificationsKeys.updateSlack(code),
  });
  const suprSendClient = new SuprSend(env.VITE_SUPRSEND_PUBLIC_KEY);
  const { data } = useUser();
  const { data: user } = useGetSuprSendUser();
  const queryClient = useQueryClient();
  const { mutate: connectChannel } = useConnectChannel();
  const { mutate: disconnectChannel } = useDisconnectChannel();

  useEffect(() => {
    if (!code) return;

    updateSlack(code, {
      onSuccess: () => {
        removeQueryString();
        connectChannel(NOTIFICATION_CHANNEL.SLACK);
      },
    });
  }, [code]);

  const slackAuthParams = new URLSearchParams({
    client_id: env.VITE_SLACK_SUPRSEND_CLIENT_ID,
    redirect_uri: `${env.VITE_BASE_URL}/notifications/settings`,
    scope:
      "chat:write,users:read,users:read.email,channels:read,im:write,chat:write.public",
  });

  const disconnectSlack = async () => {
    const email = data?.user.email;

    if (!email) return;

    setLoading(true);
    const slackData = user?.$slack?.find(
      (config: { value_json: { access_token: string } }) =>
        config.value_json.access_token,
    );

    await suprSendClient.identify(email);

    await suprSendClient.user.removeSlack({
      access_token: slackData?.value_json.access_token,
      email: slackData?.value_json.email,
      user_id: slackData?.value_json.user_id,
    });

    await queryClient.invalidateQueries({
      queryKey: notificationsKeys.user(email),
    });
    disconnectChannel(NOTIFICATION_CHANNEL.SLACK);
    setLoading(false);
  };

  return (
    <>
      {isMutating ? <ModalSlack /> : null}
      {user?.$slack?.length ? (
        <DisconnectButton
          isDisabled={isDisabled}
          loading={loading}
          onPress={disconnectSlack}
        />
      ) : (
        <a
          aria-disabled={isDisabled}
          href={`https://slack.com/oauth/v2/authorize?${slackAuthParams.toString()}`}
          rel="noreferrer"
        >
          <ConnectButton isDisabled={isDisabled} onPress={() => {}} />
        </a>
      )}
    </>
  );
}
